/**
 * 🎯 V182.5: Age Gate Screen (18+ Verification)
 * Google Play Store compliance for age-restricted content
 * - Full-screen overlay before main app
 * - Two options: "I am 18+" or "I am under 18"
 * - Stable ineligible screen on underage answer (24h local cooldown)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ShieldAlert, Link2, Users, CheckCircle, XCircle, Check } from 'lucide-react';
import {
  normalizeLanguageCode,
  resolveDocumentDirection,
} from '@/i18n/languages';

interface AgeGateScreenProps {
  onVerified: () => void | Promise<void>;
  /** Called when user selects "I am under 18" — persist denial; do not navigate. */
  onDenied: () => void | Promise<void>;
  /** Sign out + return to /auth. Must throw/reject on sign-out failure. */
  onGoBack: () => void | Promise<void>;
  /** Open directly on the ineligible screen (active cooldown / denied status). */
  initialDenied?: boolean;
  /** False during auth flicker with no user id — keep the overlay, block verify/deny/exit. */
  actionsEnabled?: boolean;
}

const brandLtr = <span dir="ltr" className="inline-block" />;

const overlayClassName =
  'age-gate-overlay h-full min-h-full w-full bg-[var(--app-bg)]';
const shellClassName =
  'age-gate-shell min-h-full w-full flex flex-col justify-center';

function AgeGateFrame({
  dir,
  lang,
  children,
}: {
  dir: ReturnType<typeof resolveDocumentDirection>;
  lang: string;
  children: React.ReactNode;
}) {
  return (
    <div
      dir={dir}
      lang={lang}
      data-testid="age-gate-root"
      className={overlayClassName}
    >
      <div className={shellClassName}>{children}</div>
    </div>
  );
}

export const AgeGateScreen: React.FC<AgeGateScreenProps> = ({
  onVerified,
  onDenied,
  onGoBack,
  initialDenied = false,
  actionsEnabled = true,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const dir = resolveDocumentDirection(language);
  const lang = normalizeLanguageCode(language) ?? 'en';

  const [loading, setLoading] = useState(false);
  const [denyLoading, setDenyLoading] = useState(false);
  const [goBackLoading, setGoBackLoading] = useState(false);
  const [showDenied, setShowDenied] = useState(initialDenied);
  const [exitError, setExitError] = useState<string | null>(null);
  const exitInFlightRef = useRef(false);

  useEffect(() => {
    if (initialDenied) {
      setShowDenied(true);
    }
  }, [initialDenied]);

  const handleConfirmAge = async () => {
    if (!actionsEnabled) return;
    console.log('🎯 V182.5: User confirmed 18+ age');
    setLoading(true);

    try {
      await onVerified();
    } catch (error) {
      console.error('🎯 V182.5: Error confirming age:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDenyAge = async () => {
    if (!actionsEnabled) return;
    console.log('🎯 V182.5: User denied 18+ age (under 18)');
    setDenyLoading(true);
    setShowDenied(true);
    setExitError(null);

    try {
      await onDenied();
    } catch (error) {
      console.error('🎯 V182.5: Error recording age denial:', error);
    } finally {
      setDenyLoading(false);
    }
  };

  const handleGoBack = async () => {
    if (!actionsEnabled) return;
    if (exitInFlightRef.current) return;
    exitInFlightRef.current = true;
    setGoBackLoading(true);
    setExitError(null);

    try {
      await onGoBack();
    } catch (error) {
      console.error('🎯 V182.5: Error on age-gate return to sign in:', error);
      setExitError(t('ageGate.exitError'));
    } finally {
      exitInFlightRef.current = false;
      setGoBackLoading(false);
    }
  };

  if (showDenied) {
    return (
      <AgeGateFrame dir={dir} lang={lang}>
        <div className="age-gate-card bg-[var(--app-bg-card)] dark:bg-gray-900 rounded-2xl shadow-2xl p-8 text-start border border-gray-200 dark:border-gray-800">
          <div className="mb-6">
            <div className="mx-auto w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <XCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 text-start">
            <Trans i18nKey="ageGate.deniedTitle" components={{ brand: brandLtr }} />
          </h1>

          <p className="text-gray-600 dark:text-gray-400 mb-6 text-start">
            <Trans i18nKey="ageGate.deniedBody" components={{ brand: brandLtr }} />
          </p>

          {exitError && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400 mb-4">
              {exitError}
            </p>
          )}

          <button
            type="button"
            onClick={handleGoBack}
            disabled={!actionsEnabled || goBackLoading || denyLoading}
            className="w-full min-h-12 bg-gray-900 dark:bg-gray-100 hover:bg-gray-800 dark:hover:bg-white text-white dark:text-gray-900 font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {goBackLoading ? t('ageGate.pleaseWait') : t('ageGate.returnToSignIn')}
          </button>
        </div>
      </AgeGateFrame>
    );
  }

  return (
    <AgeGateFrame dir={dir} lang={lang}>
      <div className="age-gate-card bg-[var(--app-bg-card)] dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-purple-800/30">
        <div className="bg-gradient-to-r rtl:bg-gradient-to-l from-purple-600 to-pink-600 dark:from-purple-700 dark:to-pink-700 px-6 py-5 text-start">
          <div className="mx-auto w-16 h-16 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <ShieldAlert className="h-8 w-8 text-purple-600 dark:text-purple-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2 text-start">
            {t('ageGate.title')}
          </h1>
          <p className="text-purple-100 dark:text-purple-200 text-sm text-start">
            {t('ageGate.subtitle')}
          </p>
        </div>

        <div className="p-6 bg-[var(--app-bg-card)] dark:bg-gray-900 space-y-5">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4 sm:p-5">
            <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2 flex items-center gap-2 text-start">
              <ShieldAlert className="h-4 w-4 shrink-0" />
              {t('ageGate.whyTitle')}
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-200 text-start leading-relaxed">
              <Trans i18nKey="ageGate.whyBody" components={{ brand: brandLtr }} />
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 text-start">
              {t('ageGate.benefitsTitle')}
            </h3>
            <div className="space-y-2">
              <div className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300 text-start">
                <Link2 className="h-4 w-4 mt-0.5 text-pink-500 dark:text-pink-400 shrink-0" />
                <span>{t('ageGate.benefitMeet')}</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300 text-start">
                <Users className="h-4 w-4 mt-0.5 text-purple-500 dark:text-purple-400 shrink-0" />
                <span>{t('ageGate.benefitTrust')}</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300 text-start">
                <CheckCircle className="h-4 w-4 mt-0.5 text-green-500 dark:text-green-400 shrink-0" />
                <span>{t('ageGate.benefitSafe')}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 sm:p-5 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-600 dark:text-gray-400 text-start leading-relaxed">
              <strong className="text-gray-900 dark:text-gray-200">{t('ageGate.privacyLead')}</strong>{' '}
              {t('ageGate.privacyBody')}
            </p>
          </div>

          <div className="space-y-3">
            <button
              type="button"
              onClick={handleConfirmAge}
              disabled={!actionsEnabled || loading || denyLoading}
              className="w-full min-h-12 flex flex-row items-center justify-center gap-2 bg-gradient-to-r rtl:bg-gradient-to-l from-purple-600 to-pink-600 dark:from-purple-700 dark:to-pink-700 hover:from-purple-700 hover:to-pink-700 dark:hover:from-purple-600 dark:hover:to-pink-600 text-white font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  {t('ageGate.verifying')}
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {t('ageGate.confirmCta')}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleDenyAge}
              disabled={!actionsEnabled || loading || denyLoading}
              className="w-full min-h-12 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 font-semibold py-4 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-2 border-gray-300 dark:border-gray-600"
            >
              {denyLoading ? t('ageGate.pleaseWait') : t('ageGate.denyCta')}
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-start">
            {t('ageGate.legal')}
          </p>
        </div>
      </div>
    </AgeGateFrame>
  );
};
